import {Color} from '@benchling/nucleus-ui';
import {UnstyledButton, Flex, Text} from '@mantine/core';
import {useDisclosure} from '@mantine/hooks';
import reactUpdate from 'immutability-helper';
import React, {memo, useState} from 'react';
import {v4 as uuidv4} from 'uuid';

import {EMPTY_MRV} from 'chemistry/ChemistryConstants';
import {parseReactionComponents} from 'chemistry/ChemistryDAO';
import {ChemicalStructureDrawerValue} from 'chemistry/marvinjs-wrappers/ChemicalStructureDrawer';
import {
  ChemistryReactionRole,
  CHEMISTRY_REACTION_ROLE_TO_DISPLAY_NAME,
} from 'notebook/integrations/chemistry/ChemistryStructuredTableConstants';
import ReactionSchemeContent from 'notebook/integrations/chemistry/ReactionSchemeContent';
import ReactionSketcherModal from 'notebook/integrations/chemistry/ReactionSketcherModal';
import {
  StoichiometryStructuredTableRowMetadata,
  StoichiometryStructuredTable,
  StoichiometryTableCustomSlotProps,
} from 'notebook/integrations/chemistry/StoichiometryStructuredTableAdapter';
import {StructuredTableColumn, StructuredTableRow} from 'notebook/structured-tables/StructuredTableConstants';
import getBlankRow from 'notebook/structured-tables/util/getBlankRow';
import {BaseTypedValueType} from 'notebook/tables/TableConstants';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import {withLoadingScreen} from 'util/Promises';

async function handleReactionSketcherSubmit(
  mrvStructure: string,
  structuredTable: StoichiometryStructuredTable,
  updateHandler: (structuredTable: StoichiometryStructuredTable) => void
): Promise<void> {
  // Parse the reaction into its components.
  const {externalFile, reactants, products} = await parseReactionComponents(mrvStructure);

  // TODO: use constants or some more stable approach to identify columns.
  const nameColumnIndex = structuredTable.columns.findIndex((col) => col.name === 'Compound');
  const roleColumnIndex = structuredTable.columns.findIndex((col) => col.name === 'Role');

  if (nameColumnIndex === -1 || roleColumnIndex === -1) {
    throw new Error('Could not find required columns');
  }

  const reactantRows = createRowsForReactionComponent(
    reactants,
    ChemistryReactionRole.REACTANT,
    nameColumnIndex,
    roleColumnIndex,
    structuredTable.columns,
    0
  );

  const productRows = createRowsForReactionComponent(
    products,
    ChemistryReactionRole.PRODUCT,
    nameColumnIndex,
    roleColumnIndex,
    structuredTable.columns,
    reactantRows.length
  );

  // Overwrite the table rows and store the external file reference in metadata
  updateHandler({
    ...structuredTable,
    metadata: {
      ...structuredTable.metadata,
      reactionMrvExternalFile: {
        id: externalFile.id,
        url: externalFile.url,
      },
    },
    rows: [...reactantRows, ...productRows],
  });
}

function createRowsForReactionComponent(
  components: Array<{moleculeId: string; name: string}>,
  role: ChemistryReactionRole,
  nameColumnIndex: number,
  roleColumnIndex: number,
  columns: Array<StructuredTableColumn>,
  startRowIndex: number
): Array<StructuredTableRow<StoichiometryStructuredTableRowMetadata>> {
  const rows: Array<StructuredTableRow<StoichiometryStructuredTableRowMetadata>> = [];
  let rowIndex = startRowIndex;

  for (const component of components) {
    const blankCells = getBlankRow(rowIndex, columns, null);
    const cells = reactUpdate(blankCells, {
      [nameColumnIndex]: {
        text: {$set: component.name},
        typedValue: {
          $set: {
            type: BaseTypedValueType.BIOENTITY,
            bioentitySummary: {id: component.moleculeId, name: component.name},
          },
        },
      },
      [roleColumnIndex]: {
        text: {$set: CHEMISTRY_REACTION_ROLE_TO_DISPLAY_NAME[role]},
        typedValue: {
          $set: {type: BaseTypedValueType.STRING, rawValue: CHEMISTRY_REACTION_ROLE_TO_DISPLAY_NAME[role]},
        },
      },
    });

    rows.push({
      cells,
      rowId: uuidv4(),
      isFinalized: false,
      metadata: {},
      lastSyncedUpstreamValues: null,
    });
    rowIndex++;
  }

  return rows;
}

/** A chemical sketcher rendered inside a Stoichiometry Structured Table. It can be used to populate the
 * table with reactants and products.
 */
function StoichiometryTableEmbeddedChemicalSketcher(props: StoichiometryTableCustomSlotProps): JSX.Element {
  const [isSketcherModalOpen, {open: openSketcherModal, close: closeSketcherModal}] = useDisclosure(false);
  const [marvinStructure, setMarvinStructure] = useState<ChemicalStructureDrawerValue>(EMPTY_MRV);
  const reactionMrvExternalFile = props.structuredTable.metadata.reactionMrvExternalFile;

  return (
    <>
      {isSketcherModalOpen && (
        <ReactionSketcherModal
          initialMarvinStructure={marvinStructure}
          onClose={closeSketcherModal}
          onSubmit={async (newMarvinStructure: string): Promise<void> => {
            closeSketcherModal();
            setMarvinStructure(newMarvinStructure);
            await withLoadingScreen(
              handleReactionSketcherSubmit(newMarvinStructure, props.structuredTable, props.updateHandler)
            );
          }}
        />
      )}

      <UnstyledButton
        role="button"
        tabIndex={0}
        aria-label="Open reaction sketcher"
        // Should this button be disabled while the reaction SVG is loading?
        onClick={openSketcherModal}
        h="150px"
        w="100%"
        style={{
          border: `1px solid ${Color.GRAY_250}`,
          borderTop: 'none',
        }}
      >
        <Flex direction="column" justify="center" align="center" h="100%">
          {reactionMrvExternalFile != null ? (
            <ReactionSchemeContent
              externalFileId={reactionMrvExternalFile.id}
              externalFileUrl={reactionMrvExternalFile.url}
              // The sketcher is always expected to be rendered within an Entry so fileId should not be null
              entryId={props.structuredTable.fileId!}
              setMarvinStructureContent={setMarvinStructure}
            />
          ) : (
            <>
              <BenchlingIcon
                icon="molecule"
                style={{width: '24px', height: '24px', color: Color.GRAY_600}}
                altText="Molecule"
              />
              <Text size="xl" color="dimmed">
                Click to start sketching
              </Text>
            </>
          )}
        </Flex>
      </UnstyledButton>
    </>
  );
}

export default memo(StoichiometryTableEmbeddedChemicalSketcher);
